import React, { useEffect, useRef } from 'react';
import { Clock, Monitor, Award, Download } from 'lucide-react';
import { Link } from 'react-router-dom';
import { courses } from '../data/mockData';

const CourseCard = ({ course, index }) => {
  const cardRef = useRef(null);

  useEffect(() => {
    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (isMobile || prefersReducedMotion) {
      if (cardRef.current) {
        cardRef.current.style.opacity = '1';
        cardRef.current.style.transform = 'translateY(0)';
      }
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && cardRef.current) {
          cardRef.current.style.opacity = '1';
          cardRef.current.style.transform = 'translateY(0)';
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.1 }
    );

    const currentCard = cardRef.current;
    if (currentCard) observer.observe(currentCard);
    return () => {
      if (currentCard) observer.unobserve(currentCard);
    };
  }, []);

  const isComingSoon = course.status === 'Coming Soon';

  const statusBadge =
    course.status === 'Enrolling'
      ? 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
      : 'bg-slate-700/50 text-slate-400 border border-slate-600/30';

  return (
    <div
      ref={cardRef}
      className={`group flex flex-col overflow-hidden rounded-xl border border-slate-700/50 bg-slate-800/40 hover:bg-slate-800/70 hover:border-teal-500/30 transition-all duration-500 ${
        isComingSoon ? 'opacity-60' : ''
      }`}
      style={{
        opacity: 0,
        transform: 'translateY(20px)',
        transitionDelay: `${(index % 3) * 100}ms`,
      }}
    >
      {/* Stripe */}
      <div className="h-[3px] flex-shrink-0" style={{ background: course.stripeColor }} />

      {/* Poster */}
      <div className="relative h-40 overflow-hidden bg-slate-950 flex-shrink-0">
        <img
          src={course.image}
          alt={course.title}
          className="w-full h-full object-contain opacity-70 group-hover:opacity-85 group-hover:scale-105 transition-all duration-600"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-transparent to-slate-900 pointer-events-none" />
        <span className="absolute top-3 left-3 z-10 px-2 py-0.5 text-[10px] font-mono tracking-widest uppercase bg-slate-950/60 text-slate-300 backdrop-blur-sm rounded-sm">
          Course {course.number}
        </span>
        <span className={`absolute top-3 right-3 z-10 px-2 py-0.5 text-[10px] font-mono tracking-widest uppercase rounded-sm ${statusBadge}`}>
          {course.status}
        </span>
      </div>

      {/* Body */}
      <div className="flex-1 p-5">
        <div className="text-[10px] font-mono tracking-[0.18em] uppercase text-teal-400/70 mb-1.5">
          {course.topic}
        </div>
        <h3 className="text-base font-bold text-white mb-2 leading-snug">
          {course.title}
        </h3>
        <p className="text-xs text-slate-400 leading-relaxed mb-3">
          {course.description}
        </p>
        <div className="flex gap-3 flex-wrap mb-3">
          <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <Clock className="w-3 h-3" />
            {course.duration}
          </span>
          <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
            <Monitor className="w-3 h-3" />
            {course.format}
          </span>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono tracking-wider uppercase text-teal-400/80 bg-teal-500/10 border border-teal-500/20 rounded-sm">
            <Award className="w-2.5 h-2.5" />
            Certificate of Completion
          </span>
          {course.brochure && (
            <a
              href={course.brochure}
              download
              className="inline-flex items-center gap-1.5 px-2 py-0.5 text-[10px] font-mono tracking-wider uppercase text-slate-300 hover:text-teal-300 bg-slate-700/40 hover:bg-slate-700/70 border border-slate-600/40 hover:border-teal-500/40 rounded-sm transition-colors"
            >
              <Download className="w-2.5 h-2.5" />
              Download Brochure
            </a>
          )}
        </div>
      </div>

      {/* Cohort */}
      <div className="px-5 py-3 bg-slate-800/60 border-t border-slate-700/40">
        <div className="font-mono text-xs font-bold text-white">{course.cohort}</div>
        <div className="text-[11px] text-slate-500">{course.cohortLabel}</div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between gap-3 px-5 py-3 border-t border-slate-700/40">
        {course.price ? (
          <div>
            <div className="font-mono text-[9px] tracking-wider uppercase text-slate-500 mb-0.5">
              Tuition
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-lg font-bold text-white">{course.price}</span>
              {course.proPrice && (
                <span className="font-mono text-xs text-teal-400">Pro {course.proPrice}</span>
              )}
            </div>
          </div>
        ) : (
          <div />
        )}
        {!course.hideCTA && course.status === 'Enrolling' && (
          <Link
            to={`/enroll/${course.id}`}
            className="min-h-[44px] px-5 py-2.5 bg-teal-500 hover:bg-teal-400 text-slate-950 text-[10px] font-mono font-semibold tracking-wider uppercase transition-all rounded-sm hover:shadow-lg hover:shadow-teal-500/20 hover:-translate-y-0.5 active:translate-y-0 inline-flex items-center"
          >
            Enroll Now
          </Link>
        )}
        {!course.hideCTA && course.status === 'Coming Soon' && (
          <button className="min-h-[44px] px-5 py-2.5 border border-slate-600 text-slate-400 hover:text-slate-300 hover:border-slate-500 text-[10px] font-mono tracking-wider uppercase transition-all rounded-sm">
            Notify Me
          </button>
        )}
      </div>
    </div>
  );
};

const CoursesSection = () => {
  const activeCount = courses.length;
  const enrollingCount = courses.filter((c) => c.status === 'Enrolling').length;
  return (
    <section id="courses" className="py-32 px-6 lg:px-12 bg-slate-950">
      <div className="max-w-[1400px] mx-auto">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-5xl lg:text-6xl font-bold text-white mb-6">
            GIS & Geospatial Courses
          </h2>
          <p className="text-xl text-slate-300 max-w-3xl mx-auto leading-relaxed">
            Practical, instructor-led courses in GIS, remote sensing, and GeoAI — designed for
            students, researchers, and working professionals
          </p>
        </div>

        {/* Stats Row */}
        <div className="flex justify-center gap-12 md:gap-20 mb-12">
          <div className="text-center">
            <div className="text-4xl font-bold text-teal-400">{activeCount}</div>
            <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">Active Courses</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-teal-400">Live</div>
            <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">Cohorts</div>
          </div>
          <div className="text-center">
            <div className="text-4xl font-bold text-teal-400">100%</div>
            <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">Certified</div>
          </div>
        </div>

        {/* Live Status Bar */}
        <div className="flex items-center gap-4 flex-wrap px-5 py-3 mb-10 bg-slate-800/50 border border-slate-700/50 rounded-lg">
          <div className="w-2 h-2 rounded-full bg-teal-400 animate-pulse flex-shrink-0" />
          <span className="font-mono text-[11px] tracking-wider uppercase text-teal-400">
            Enrollments Open
          </span>
          <div className="w-px h-4 bg-slate-700 hidden sm:block" />
          <span className="text-sm text-slate-400">
            Next cohort starts <strong className="text-white">May 12, 2026</strong>
          </span>
          <div className="w-px h-4 bg-slate-700 hidden sm:block" />
          <span className="text-sm text-slate-400">
            <strong className="text-white">{enrollingCount} of {activeCount}</strong> courses enrolling now
          </span>
          <span className="ml-auto px-3 py-1 font-mono text-[10px] tracking-wider uppercase text-teal-400 bg-teal-500/10 border border-teal-500/20 rounded-sm">
            15% Early Discount
          </span>
        </div>

        {/* Course Grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-10">
          {courses.map((course, index) => (
            <CourseCard key={course.id} course={course} index={index} />
          ))}
        </div>

        {/* Early Enroll Band */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 px-8 py-6 bg-teal-500/10 border border-teal-500/20 rounded-xl relative overflow-hidden">
          <p className="text-sm text-slate-300 leading-relaxed">
            <strong className="text-teal-400">Early Enrollment — 15% off all open courses until April 30, 2026.</strong>{' '}
            Use code <strong className="text-white">SPATIAL15</strong> at checkout. Includes live
            instructor sessions, real-world project datasets, and a certificate of completion.
          </p>
          <button className="flex-shrink-0 px-6 py-3 bg-teal-500 hover:bg-teal-400 text-slate-950 font-semibold text-sm transition-colors rounded-lg">
            Enroll Early & Save
          </button>
        </div>
      </div>
    </section>
  );
};

export default CoursesSection;
